/**
 * Backups of farmers still open on this phone (delivery drafts) to the server, so the office can
 * see the gate live and nothing is lost if the phone loses its data.
 *
 * Deliberately beside the purchase flow, never in it:
 *  - not in the outbox: a backup never waits behind, or holds up, a purchase record;
 *  - never throws and is never awaited by a screen: a failed backup changes nothing on the phone;
 *  - only while the server's roster says `draft_backup` (an older server never gets the calls);
 *  - completing or cancelling a farmer works exactly as before: the server reads that from the lot.
 *
 * Sent ~3 s after a farmer is saved, every 30 s, when the network returns and when the app goes to
 * the background. Each farmer goes again only when its saved copy changed (`updated_at`).
 */
import { api } from '@/api'
// Direct module imports, not '@/offline': SyncProvider (in '@/offline') starts this module.
import { getDb, type SupplyChainDB } from '@/offline/db'
import { isDemoDevice } from '@/offline/device'
import { getMeta, setMeta } from '@/offline/meta'
import { roster } from '@/offline/roster'
import { listDrafts } from '@/offline/drafts'
import { DRAFT_KIND, type DeliveryDraft } from '../model/delivery'

const DEBOUNCE_MS = 3_000
const INTERVAL_MS = 30_000
const MAX_PER_CALL = 50

let timer: ReturnType<typeof setTimeout> | null = null
let running: Promise<void> | null = null
/** The server said backups are off: stop until the app restarts. */
let serverOff = false

/** After a farmer is saved: back it up shortly. Safe to call from anywhere; never throws. */
export function scheduleDraftBackup(): void {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    timer = null
    void flushDraftBackup()
  }, DEBOUNCE_MS)
}

/** Send every open farmer that changed since it was last sent. Never throws. */
export function flushDraftBackup(db: SupplyChainDB = getDb()): Promise<void> {
  if (running) return running
  running = send(db)
    .catch(() => undefined)
    .finally(() => {
      running = null
    })
  return running
}

async function send(db: SupplyChainDB): Promise<void> {
  if (serverOff || (typeof navigator !== 'undefined' && navigator.onLine === false)) return
  const device = await getMeta('device', db)
  if (!device || device.status !== 'ACTIVE' || isDemoDevice(device)) return
  const config = await roster.gateConfig(db)
  if (!config?.draft_backup) return

  const drafts = await listDrafts<DeliveryDraft>(DRAFT_KIND, db)
  const sent = (await getMeta('draft_backup_sent', db)) ?? {}
  const changed = drafts.filter((r) => r.data && r.updated_at > (sent[r.id] ?? 0))

  for (let i = 0; i < changed.length; i += MAX_PER_CALL) {
    const batch = changed.slice(i, i + MAX_PER_CALL)
    const res = (await api.post(
      '/devices/me/delivery-drafts',
      {
        drafts: batch.map((r) => ({ delivery_ulid: r.id, version: r.updated_at, data: r.data })),
      },
      { timeoutMs: 20_000 },
    )) as { enabled?: boolean } | null
    if (res && res.enabled === false) {
      serverOff = true
      return
    }
    // Saved or ignored (the server already had this copy or a newer one): either way it is there.
    for (const r of batch) sent[r.id] = r.updated_at
  }

  // Forget farmers no longer open on this phone (completed or cancelled).
  const open = new Set(drafts.map((r) => r.id))
  for (const id of Object.keys(sent)) if (!open.has(id)) delete sent[id]
  await setMeta('draft_backup_sent', sent, db)
}

/** Start the background triggers (once, with the sync engine). Returns a stop function. */
export function startDraftBackup(): () => void {
  const kick = () => void flushDraftBackup()
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') kick()
  }
  const interval = setInterval(kick, INTERVAL_MS)
  window.addEventListener('online', kick)
  document.addEventListener('visibilitychange', onVisibility)
  kick()
  return () => {
    clearInterval(interval)
    window.removeEventListener('online', kick)
    document.removeEventListener('visibilitychange', onVisibility)
    if (timer) clearTimeout(timer)
    timer = null
  }
}
